// ─── WEEK / MONTH LOGIC ──────────────────────────────────────────────────────

export function getWeekStart(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day - 4 + 7) % 7;
  const thu = new Date(d);
  thu.setDate(d.getDate() - diff);
  thu.setHours(5, 0, 0, 0);
  if (thu > d) thu.setDate(thu.getDate() - 7);
  return thu;
}

export function getNextWeekStart(date = new Date()) {
  const ws = getWeekStart(date);
  ws.setDate(ws.getDate() + 7);
  return ws;
}

export function getWeekKey(date = new Date()) {
  return getWeekStart(date).toISOString().slice(0, 10);
}

export function getMonthStart(date = new Date()) {
  const d = new Date(date);
  const mStart = new Date(d.getFullYear(), d.getMonth(), 1);
  const day = mStart.getDay();
  const diff = (day - 4 + 7) % 7;
  const thu = new Date(d.getFullYear(), d.getMonth(), 1 - diff);
  thu.setHours(14, 0, 0, 0);
  return thu;
}

export function getMonthKey(date = new Date()) {
  const d = new Date(date);
  const ms = getMonthStart(d);
  if (d < ms) {
    const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function getNextMonthStart(date = new Date()) {
  const mk = getMonthKey(date);
  const [y, m] = mk.split("-").map(Number);
  const nm = m === 12 ? 1 : m + 1;
  const ny = m === 12 ? y + 1 : y;
  return getMonthStart(new Date(ny, nm - 1, 15));
}

export function formatCountdown(ms) {
  if (ms <= 0) return "NOW";
  const totalSec = Math.floor(ms / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  return `${m}m ${s}s`;
}

export function fmtWeekKey(wk) {
  const d = new Date(wk + "T14:00:00");
  return d.toLocaleDateString([], { month: "short", day: "numeric" }) + " – " +
    new Date(d.getTime() + 7 * 86400000).toLocaleDateString([], { month: "short", day: "numeric" });
}

export function fmtMonthKey(mk) {
  const [y, m] = mk.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString([], { month: "long", year: "numeric" });
}

// ─── STATS ────────────────────────────────────────────────────────────────────

function empty() { return { tags: 0, referrals: 0, oneOnOnes: 0, guests: 0, closedRx: 0, closedTx: 0 }; }

export function computeStats(events) {
  const p = {};
  const g = n => { if (!p[n]) p[n] = empty(); return p[n]; };
  for (const e of events) {
    if (e.type === "tag") g(e.receiver).tags++;
    else if (e.type === "referral") g(e.giver).referrals++;
    else if (e.type === "oneOnOne") { g(e.a).oneOnOnes++; g(e.b).oneOnOnes++; }
    else if (e.type === "guest") g(e.bringer).guests++;
    else if (e.type === "biz") { g(e.receiver).closedRx += e.amt; g(e.sender).closedTx += e.amt; }
  }
  return p;
}

export function topOf(stats, key) {
  let best = null, bv = 0;
  for (const [n, s] of Object.entries(stats)) if (s[key] > bv) { bv = s[key]; best = n; }
  return best ? { name: best, val: bv } : null;
}

export const CATS = [
  { key: "tags",      label: "Social Tags",       icon: "Tag",       money: false },
  { key: "referrals", label: "Referrals Given", icon: "Handshake", money: false },
  { key: "oneOnOnes", label: "1-on-1s",            icon: "Coffee",    money: false },
  { key: "guests",    label: "Guests",             icon: "Star",      money: false },
  { key: "closedRx",  label: "Closed (Received)",  icon: "DollarSign",money: true  },
  { key: "closedTx",  label: "Closed (Sent)",      icon: "Send",      money: true  },
];

export const fmtVal = (v, money) => money ? `$${Number(v).toLocaleString()}` : String(v);

export function describeEvent(e) {
  if (e.type === "tag") return `${e.giver && e.giver !== "Unknown" ? e.giver + " tagged " : ""}${e.receiver} received a tag`;
  if (e.type === "referral") return `${e.giver} gave a referral to ${e.receiver}`;
  if (e.type === "oneOnOne") {
    if (e.a === e.b) return `${e.a} had a 1-on-1${e.note ? " — " + e.note : " (with guest/non-member)"}`;
    return `${e.a} & ${e.b} had a 1-on-1`;
  }
  if (e.type === "guest") {
    const gName = e.guestName || "a guest";
    if (e.visitStatus === "scheduled") {
      const dateStr = e.visitDate ? new Date(e.visitDate + "T12:00:00").toLocaleDateString([], { month: "short", day: "numeric" }) : "";
      return `${gName} is scheduled to visit${dateStr ? " " + dateStr : ""} · invited by ${e.bringer}`;
    }
    if (e.visitStatus === "visited") {
      const dateStr = e.visitDate ? new Date(e.visitDate + "T12:00:00").toLocaleDateString([], { month: "short", day: "numeric" }) : "";
      return `${gName} visited${dateStr ? " " + dateStr : ""} · invited by ${e.bringer}`;
    }
    return `${e.bringer} brought ${gName}`;
  }
  if (e.type === "biz") return `${e.receiver} closed $${Number(e.amt).toLocaleString()} · sent by ${e.sender}${e.notes ? " — " + e.notes : ""}`;
  return "Event";
}

export const EVENT_TYPES = [
  { key: "tag",      label: "Social Tag",   color: "orange" },
  { key: "referral", label: "Referral",      color: "blue" },
  { key: "oneOnOne", label: "1-on-1",        color: "purple" },
  { key: "guest",    label: "Guest",         color: "green" },
  { key: "biz",      label: "Closed $",      color: "red" },
];

export function getEventColor(type) {
  return {
    tag:      { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-200", btn: "bg-orange-600 hover:bg-orange-700" },
    referral: { bg: "bg-blue-100",   text: "text-blue-700",   border: "border-blue-200",   btn: "bg-blue-600 hover:bg-blue-700" },
    oneOnOne: { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-200", btn: "bg-purple-600 hover:bg-purple-700" },
    guest:    { bg: "bg-green-100",  text: "text-green-700",  border: "border-green-200",  btn: "bg-green-600 hover:bg-green-700" },
    biz:      { bg: "bg-red-100",    text: "text-red-700",    border: "border-red-200",    btn: "bg-red-600 hover:bg-red-700" },
  }[type] || { bg: "bg-zinc-100", text: "text-zinc-700", border: "border-zinc-200", btn: "bg-zinc-600" };
}
